// src/context/AudioProvider.tsx
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { Howl, Howler } from "howler";
import { AudioState, AudioAction, Track, AudioContextProps } from "@/context/audioTypes";
import { audioReducer, initialAudioState } from "@/context/audioReducer";
import { useAuth } from "@/context/AuthProvider";
import { getSignedAudioUrl } from "@/services/mediaService";
import LoginPromptModal from "@/components/common/LoginPromptModal";
import { 
  logAnalyticsEvent, 
  logTrackComplete, 
  logPause, 
  logResume, 
  logSkip,
  resetSession,
  handleTrackTransition
} from "@/services/loggingService";

// Define new interface for worker messages
interface WorkerMessage {
  type: string;
  data: any;
}

const AudioContext = createContext<AudioContextProps | undefined>(undefined);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(audioReducer, initialAudioState);
  const [volume, setVolume] = useState(0.75);
  const [muted, setMuted] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const soundRef = useRef<Howl | null>(null);
  const animationRef = useRef<number | null>(null);
  const { user } = useAuth();
  
  // Add worker reference
  const audioWorkerRef = useRef<Worker | null>(null);
  const [workerReady, setWorkerReady] = useState(false);

  // Use a more stable reference for tracking that doesn't change with re-renders
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const trackInfoRef = useRef({
    trackId: null as string | null,
    startTime: null as number | null,
    isFirstPlay: true,
  });

  // Initialize Web Worker
  useEffect(() => {
    try {
      // Create worker
      audioWorkerRef.current = new Worker(new URL('../workers/audioWorker.ts', import.meta.url));
      
      // Set up message handler for worker
      audioWorkerRef.current.onmessage = (e: MessageEvent<WorkerMessage>) => {
        const { type, data } = e.data;
        
        switch (type) {
          case 'WORKER_READY':
            setWorkerReady(true);
            break;
          
          case 'PROGRESS_UPDATE':
            dispatch({ type: "SET_PROGRESS", progress: data.progress });
            break;
            
          case 'LOAD_NEXT_CHUNK':
            // Handle loading next chunk (implementation would depend on backend)
            console.log('Need to load next chunk at position:', data.current);
            break;
            
          default:
            console.warn('Unknown message from worker:', type);
        }
      };
      
      // Handle worker errors
      audioWorkerRef.current.onerror = (error) => {
        console.error('Audio worker error:', error);
        setWorkerReady(false);
      };
    } catch (error) {
      console.error('Failed to initialize audio worker:', error);
    }
    
    // Clean up worker on unmount
    return () => {
      if (audioWorkerRef.current) {
        audioWorkerRef.current.terminate();
        audioWorkerRef.current = null;
      }
    };
  }, []);

  // Initialize Howler settings once
  useEffect(() => {
    // Disable auto-suspend to keep audio context active
    Howler.autoSuspend = false;
    
    // Ensure Howler's audio context is ready
    Howler.autoUnlock = true;
    
    // Initialize audio context if not already
    if (Howler.ctx?.state !== "running") {
      try {
        Howler.ctx?.resume();
      } catch {}
    }
    
    return () => {
      // Clean up on unmount
      if (soundRef.current) {
        soundRef.current.unload();
      }
    };
  }, []);

  // Update Howler volume when volume or mute state changes
  useEffect(() => {
    if (soundRef.current) {
      soundRef.current.volume(muted ? 0 : volume);
    }
  }, [volume, muted]);

  // Memoize recordPlayDuration to avoid recreation on each render
  const recordPlayDuration = useCallback(() => {
    const { trackId, startTime, isFirstPlay } = trackInfoRef.current;
    if (trackId && startTime && !isFirstPlay && stateRef.current.isPlaying) {
      const duration = (Date.now() - startTime) / 1000;
      if (duration > 1) {
        // TODO: Implement updatePlayDuration service call
        console.log(`Recording play duration: ${duration}s for track ${trackId}`);
      }
    }
    trackInfoRef.current.startTime = null;
  }, [user?.id]);

  // Memoize stopAndUnload to avoid recreation on each render
  const stopAndUnload = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (soundRef.current) {
      recordPlayDuration();
      soundRef.current.stop();
      soundRef.current.unload();
      soundRef.current = null;
    }
  }, [recordPlayDuration]);

  // Add a method to inform the worker about UI priority
  const setUIPriority = useCallback((isPrioritizeUI: boolean) => {
    // Only send message to worker, do not interfere with actual audio playback
    if (audioWorkerRef.current && workerReady) {
      audioWorkerRef.current.postMessage({
        type: 'PRIORITY_MODE',
        data: { isPrioritizeUI }
      });
    }
    
    console.log(`[AudioProvider] UI Priority mode: ${isPrioritizeUI ? 'ON' : 'OFF'} - ${new Date().toISOString()}`);
    console.log(`[AudioProvider] Audio playing: ${state.isPlaying}, Track: ${state.currentTrack?.title}`);
  }, [workerReady, state.isPlaying, state.currentTrack?.title]);

  // Optimize the update progress function to use the worker
  const updateProgress = useCallback(() => {
    if (!soundRef.current || !state.isPlaying) return;
    
    try {
      const current = soundRef.current.seek() as number;
      const duration = soundRef.current.duration();
      const buffered = duration; // Currently represents full file, would be chunk size in streaming
      
      // Use worker for progress updates if available
      if (audioWorkerRef.current && workerReady) {
        // Send minimal updates to worker to avoid overloading the messaging system
        audioWorkerRef.current.postMessage({ 
          type: 'UPDATE_PROGRESS',
          data: { currentTime: current, duration }
        });
        
        // Only monitor buffer very occasionally to reduce worker load
        if (Math.random() < 0.05) { // 5% of updates check buffer
          audioWorkerRef.current.postMessage({
            type: 'BUFFER_MONITOR',
            data: { current, buffered }
          });
        }
      } else {
        // Fallback without worker - only update if significant change
        if (!isNaN(current) && Math.abs(current - state.progress) >= 0.25) {
          dispatch({ type: "SET_PROGRESS", progress: current });
        }
      }
    } catch (err) {
      console.error("Update progress error:", err);
    }
    
    // Schedule next update using requestAnimationFrame
    if (state.isPlaying) {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, [state.isPlaying, state.progress, workerReady]);

  const getPlaylistAnalyticsContext = useCallback((track?: Track | null) => {
    if (!track) return undefined;
    const playlistId = track.playlist_id || track.source_playlist_id;
    if (!playlistId) return undefined;
    return {
      playlist_id: playlistId,
      playlist_title: track.playlist_title,
      source_playlist_id: track.source_playlist_id,
      bundle_type: track.bundle_type || "user",
    };
  }, []);

  // Log track plays
  const logTrackPlay = useCallback((track: Track) => {
    if (!track?.id) return;

    try {
      // Normalize duration to milliseconds (track.duration may be seconds or ms)
      const durationMs = typeof track.duration === 'number'
        ? (track.duration > 10000 ? Math.round(track.duration) : Math.round(track.duration * 1000))
        : undefined;

      // Mark this as first play for the new track and emit play_start
      trackInfoRef.current.isFirstPlay = true;
      const playlistContext = getPlaylistAnalyticsContext(track);
      logAnalyticsEvent({ 
        trackId: track.id,
        trackTitle: track.title,
        artistId: track.artist_id,  // ✅ Added
        artistName: track.artistName,
        eventType: 'play_start',
        trackDurationMs: durationMs,
        additionalData: playlistContext,
      });
    } catch (err) {
      console.error("Failed to log playback", err);
    }
  }, [getPlaylistAnalyticsContext]);

  // Setup Howler with optimized settings
  const setupHowler = (src: string, track: Track) => {
    stopAndUnload();
    
    // Detect format from URL or Content-Type; let Howler auto-detect when uncertain
    let format: string | undefined = undefined;

    // EXISTING FORMAT DETECTION (by file extension in URL):
    if (src.includes('.mp3')) {
      format = 'mp3';
    } else if (src.includes('.m4a')) {
      format = 'm4a';
    } else if (src.includes('.aac')) {
      format = 'aac';
    }
    // No format: Howler auto-detects from Content-Type (works for MassiveMusic and other streams)

    soundRef.current = new Howl({
      src: [src],
      format: format,
      html5: true, // Use HTML5 Audio for better streaming performance
      xhr: {
        method: 'GET',
        headers: {
          Range: 'bytes=0-' // Prepare for range requests if supported
        }
      },
      preload: true, // Preload audio but in smaller chunks due to html5: true
      volume: muted ? 0 : volume,
      loop: state.isLooping,
      onplay: () => {
        dispatch({ type: "PLAY" });
        trackInfoRef.current.startTime = Date.now();
        const playlistContext = getPlaylistAnalyticsContext(track);
        if (trackInfoRef.current.isFirstPlay) {
          // First play of this track already logged via logTrackPlay
          trackInfoRef.current.isFirstPlay = false;
        } else {
          // Only log resume if this track was previously paused
          const durationMs = typeof track.duration === 'number'
            ? (track.duration > 10000 ? Math.round(track.duration) : Math.round(track.duration * 1000))
            : undefined;
          logResume(track.id, track.title, track.artistName, durationMs, playlistContext, track.artist_id);
        }
        updateProgress();
      },
      onpause: () => {
        recordPlayDuration();
        dispatch({ type: "PAUSE" });
        
        // Log pause event
        const durationMs = typeof track.duration === 'number'
          ? (track.duration > 10000 ? Math.round(track.duration) : Math.round(track.duration * 1000))
          : undefined;
        logPause(track.id, track.title, track.artistName, durationMs, getPlaylistAnalyticsContext(track), track.artist_id);
        
        // Stop progress updates by canceling any pending animation frame
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
      },
      onend: () => {
        recordPlayDuration();
        
        // Log track completion
        const durationMs = typeof track.duration === 'number'
          ? (track.duration > 10000 ? Math.round(track.duration) : Math.round(track.duration * 1000))
          : undefined;
        logTrackComplete(track.id, track.title, track.artistName, durationMs, getPlaylistAnalyticsContext(track), track.artist_id);
        
        if (!soundRef.current?.loop()) {
          // 🔧 ENHANCED: Handle automatic track transition
          const nextIndex = (state.currentIndex + 1) % state.playlist.length;
          const nextTrack = state.playlist[nextIndex];
          
          if (nextTrack) {
            // Suppress skip logging for auto-advance
            suppressNextSkipRef.current = true;

            // Use handleTrackTransition to ensure proper session management
            const nextDurationMs = typeof nextTrack.duration === 'number'
              ? (nextTrack.duration > 10000 ? Math.round(nextTrack.duration) : Math.round(nextTrack.duration * 1000))
              : undefined;
            handleTrackTransition(
              nextTrack.id,
              nextTrack.title,
              nextTrack.artistName,
              nextDurationMs,
              'auto_advance',
              getPlaylistAnalyticsContext(nextTrack)
            );
          }
          
          dispatch({ type: "NEXT_TRACK" });
        }
      },
      onload: () => {
        try {
          // Set the duration from the Howl object when loaded
          if (soundRef.current) {
            const duration = soundRef.current.duration();
            // Only update if we got a valid duration and it differs from the track's existing value
            if (!isNaN(duration) && duration > 0 && duration !== state.duration) {
              console.log(`Track loaded with duration: ${duration}s`);
              dispatch({ type: "SET_DURATION", duration });
            }
          }
        } catch (err) {
          console.error("Failed to get track duration", err);
        }
      },
      onloaderror: (id, err) => console.error("Load error", err),
      onplayerror: (id, err) => console.error("Play error", err),
    });
  };

  const playTrack = async (track: Track, playlist: Track[]) => {
    if (!track.preview_url && !user) {
      setShowLoginPrompt(true);
      return;
    }

    const isManualPlay = !state.currentTrack || state.currentTrack.id !== track.id;
    if (isManualPlay) logTrackPlay(track);

    trackInfoRef.current = {
      trackId: track.id,
      startTime: null,
      isFirstPlay: true, // ensure first event for this track is play_start
    };

    dispatch({ type: "PLAY_TRACK", track, playlist });

    let src = track.audioUrl || track.preview_url;
    // For MassiveMusic tracks, file_path is NULL, so use track ID instead
    const filePathOrId = track.file_path || track.id;
    if (user && filePathOrId) {
      try {
        const signedUrl = await getSignedAudioUrl(filePathOrId);
        if (signedUrl) {
          src = signedUrl;
          dispatch({ type: "SET_CURRENT_TRACK_AUDIO_URL", audioUrl: signedUrl });
        } else {
          console.warn("Signed URL missing");
          return;
        }
      } catch (err) {
        console.error("Error fetching signed URL", err);
        return;
      }
    }

    if (!src) return;
    setupHowler(src, track);
    soundRef.current?.play();
  };

  const togglePlay = () => {
    if (!soundRef.current) return;
    if (state.isPlaying) {
      soundRef.current.pause();
      dispatch({ type: "PAUSE" });
    } else {
      soundRef.current.play();
      dispatch({ type: "PLAY" });
    }
  };

  const seekTo = (time: number) => {
    if (soundRef.current) {
      const wasPlaying = state.isPlaying;
      soundRef.current.seek(time);
      dispatch({ type: "SET_PROGRESS", progress: time });
      try {
        // Emit a seek event
        const t = state.currentTrack;
        if (t?.id) {
          const durationMs = typeof t.duration === 'number'
            ? (t.duration > 10000 ? Math.round(t.duration) : Math.round(t.duration * 1000))
            : undefined;
          const playlistContext = getPlaylistAnalyticsContext(t);
          logAnalyticsEvent({
            trackId: t.id,
            trackTitle: t.title,
            artistId: t.artist_id,  // ✅ Added
            artistName: t.artistName,
            eventType: 'seek',
            trackDurationMs: durationMs,
            additionalData: {
              ...(playlistContext || {}),
              seek_position_ms: Math.round(time * 1000),
              was_playing: wasPlaying,
            }
          });
        }
      } catch {}
    }
  };

  const toggleShuffle = () => {
    dispatch({ type: "TOGGLE_SHUFFLE" });
  };

  const toggleVolume = () => {
    setMuted(!muted);
    setVolume(muted ? 0.75 : 0);
  };

  useEffect(() => {
    if (soundRef.current) {
      soundRef.current.loop(state.isLooping);
    }
  }, [state.isLooping]);

  const currentTrackRef = useRef<{ id: string | null; audioUrl: string | undefined }>({ 
    id: null, 
    audioUrl: undefined 
  });

  // Track skip analytics - detect when tracks change due to next/prev actions
  const previousTrackRef = useRef<string | null>(null);
  const skipTimeoutRef = useRef<number | null>(null);
  const suppressNextSkipRef = useRef<boolean>(false);

  useEffect(() => {
    // Skip analytics: detect track changes that aren't manual plays
    if (state.currentTrack?.id && previousTrackRef.current && 
        state.currentTrack.id !== previousTrackRef.current &&
        !trackInfoRef.current.isFirstPlay) {
      
      // If we auto-advanced, don't log skip
      if (suppressNextSkipRef.current) {
        suppressNextSkipRef.current = false;
      } else {
        // Clear any existing timeout
        if (skipTimeoutRef.current) {
          clearTimeout(skipTimeoutRef.current);
        }
        
        // Set a short timeout to determine skip direction
        skipTimeoutRef.current = window.setTimeout(async () => {
          if (state.currentTrack && previousTrackRef.current) {
            const n = state.playlist.length;
            const currentIndex = state.playlist.findIndex(t => t.id === state.currentTrack?.id);
            const prevIndex = state.playlist.findIndex(t => t.id === previousTrackRef.current);
            
            if (n > 0 && currentIndex !== -1 && prevIndex !== -1) {
              // Wrap-aware minimal step direction
              const forwardSteps = (currentIndex - prevIndex + n) % n;
              const backwardSteps = (prevIndex - currentIndex + n) % n;
              const direction: 'forward' | 'backward' = forwardSteps <= backwardSteps ? 'forward' : 'backward';
              
              // 🔧 ENHANCED: Use handleTrackTransition for proper session management
              const currentDurationMs = typeof state.currentTrack.duration === 'number'
                ? (state.currentTrack.duration > 10000 ? Math.round(state.currentTrack.duration) : Math.round(state.currentTrack.duration * 1000))
                : undefined;
              await handleTrackTransition(
                state.currentTrack.id,
                state.currentTrack.title,
                state.currentTrack.artistName,
                currentDurationMs,
                'skip',
                getPlaylistAnalyticsContext(state.currentTrack)
              );
              
              // Log skip event for the previous track with duration
              const prevTrack = state.playlist[prevIndex];
              const prevDurationMs = typeof prevTrack?.duration === 'number'
                ? (prevTrack.duration > 10000 ? Math.round(prevTrack.duration) : Math.round(prevTrack.duration * 1000))
                : undefined;
              logSkip(
                previousTrackRef.current,
                direction,
                prevTrack?.title,
                prevTrack?.artistName,
                'user_skip',
                prevDurationMs,
                getPlaylistAnalyticsContext(prevTrack),
                prevTrack?.artist_id  // ✅ Added
              );
            }
          }
        }, 100);
      }
    }
    
    // Update previous track reference
    previousTrackRef.current = state.currentTrack?.id || null;
  }, [state.currentTrack?.id, state.playlist]);

  useEffect(() => {
    if (!state.currentTrack) return;

    // Compare with previous track to prevent unnecessary setups
    if (
      currentTrackRef.current.id === state.currentTrack.id &&
      currentTrackRef.current.audioUrl === state.currentTrack.audioUrl &&
      soundRef.current
    ) {
      return;
    }

    // Update ref with current track info
    currentTrackRef.current = {
      id: state.currentTrack.id,
      audioUrl: state.currentTrack.audioUrl,
    };

    const setup = async () => {
      stopAndUnload();
      
      // Safe access to currentTrack properties
      if (!state.currentTrack) return;
      
      let src = state.currentTrack.audioUrl || state.currentTrack.preview_url;
      if (user && state.currentTrack.file_path && !state.currentTrack.audioUrl) {
        const url = await getSignedAudioUrl(state.currentTrack.file_path);
        if (url) {
          dispatch({ type: "SET_CURRENT_TRACK_AUDIO_URL", audioUrl: url });
          src = url;
        }
      }
      
      if (src && state.currentTrack) {
        setupHowler(src, state.currentTrack);
        if (state.isPlaying) soundRef.current?.play();
      }
    };
    setup();
  }, [state.currentTrack?.id, state.currentTrack?.audioUrl, user]);

  // Improve the visibility change handler
  useEffect(() => {
    const resumeAudio = async () => {
      if (document.visibilityState === "visible") {
        try {
          // Resume the audio context if suspended
          if (Howler.ctx?.state === "suspended") {
            await Howler.ctx.resume();
          }
          
          // If we were playing, force a progress update
          if (state.isPlaying && soundRef.current) {
            updateProgress();
          }
        } catch (err) {
          console.error("Error resuming audio context:", err);
        }
      }
    };
    
    document.addEventListener("visibilitychange", resumeAudio);
    window.addEventListener("focus", resumeAudio);
    
    return () => {
      document.removeEventListener("visibilitychange", resumeAudio);
      window.removeEventListener("focus", resumeAudio);
    };
  }, [state.isPlaying]);

  useEffect(() => {
    if (state.currentTrack?.id && soundRef.current) {
      trackInfoRef.current.trackId = state.currentTrack.id;
    }
  }, [state.currentTrack?.id]);

  useEffect(() => {
    return () => {
      recordPlayDuration();
      stopAndUnload();
      // Cleanup skip timeout
      if (skipTimeoutRef.current) {
        clearTimeout(skipTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AudioContext.Provider
      value={{
        state,
        dispatch,
        playTrack,
        togglePlay,
        seekTo,
        toggleShuffle,
        volume,
        muted,
        setVolume,
        toggleVolume,
        setUIPriority,
      }}
    >
      {children}
      {showLoginPrompt && (
        <LoginPromptModal onClose={() => setShowLoginPrompt(false)} />
      )}
    </AudioContext.Provider>
  );
};

export const useAudio = (): AudioContextProps => {
  const context = useContext(AudioContext);
  if (!context) throw new Error("useAudio must be used within an AudioProvider");
  return context;
};

export default AudioProvider;




